import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import { useSnackbar } from "notistack";
import Form, { FormDirty, FormErrors, FormFooterAllButtons } from "../../../../components/FormControl/Form/Form";
import Panel from "../../../../components/Panel/Panel";
import PanelBodyFormContent from "../../../../components/Panel/PanelBodyFormContent";
import Task from "../../../../model/Task";
import { useAuthentication } from "../../../../reducer/authentication";
import { useEffect, useState } from "react";
import CommentTask from "../../../../model/CommentTask";
import ChatIcon from '@mui/icons-material/Chat';
import AttachmentTaskType from "../../../../enum/AttachmentTaskType";
import CommentFormDialog from "../../../../components/CommentFormDialog";
import TicketHistory, { TicketHistoryEventEmitterType } from "../../../../components/TicketHistory";
import EventEmitter from "../../../../utils/EventEmitter";
import CommentTaskType from "../../../../enum/CommentTaskType";
import TaskPostServiceStatus from "../../../../enum/TaskPostServiceStatus";
import SelectSearch from "../../../../components/SelectSearch";
import { AttachmentTaskRow, TaskAttachmentDescriptionFormDialog, TaskAttachmentList } from "./TaskForm";
import { downloadBlob } from "../../../../service/ServiceApi";
import { objectClone } from "../../../../utils";
import OrangeButton from "../../../../components/FormControl/OrangeButton";

export type TaskPostServiceFormData = {
    post_service_status: TaskPostServiceStatus;
    software_improvement_needed: number | null;
    hardware_improvement_needed: number | null;
    documentation_improvement_needed: number | null;
    comments: CommentTask[];
    attachments: AttachmentTaskRow[];
}

export interface TaskPostServiceFormProps {
    title: React.ReactNode;
    item?: Task;
    onSubmit: (data: TaskPostServiceFormData, attachmentsRemoved: AttachmentTaskRow[], isDirty?: boolean, submitType?: string | null) => Promise<any>;
    onCancel: () => any;
    onChange: (changed: boolean) => any;
    onUndo: () => any;
}

const eventEmmiter = new EventEmitter<TicketHistoryEventEmitterType>();

export default function TaskPostServiceForm(props: TaskPostServiceFormProps) {

    const { title, item, onSubmit, onCancel, onChange, onUndo } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthentication();

    const [commentDialogOpen, setCommentDialogOpen] = useState<boolean>(false);
    const [isDirty, setDirty] = useState<boolean>(false);
    const [attachmentsRemoved, setAttachmentsRemoved] = useState<AttachmentTaskRow[]>([]);
    const [attachmentsEditingIdx, setAttachmentsEditingIdx] = useState<number | null>(null);
    const [attachmentDescriptionDialogOpen, setAttachmentDescriptionDialogOpen] = useState<boolean>(false);

    useEffect(() => {

    }, []);

    const initialValues: TaskPostServiceFormData = {
        post_service_status: item?.post_service_status || TaskPostServiceStatus.NotAnalyzed,
        software_improvement_needed: item?.software_improvement_needed !== null && item?.software_improvement_needed !== undefined ? item?.software_improvement_needed : null,
        hardware_improvement_needed: item?.hardware_improvement_needed !== null && item?.hardware_improvement_needed !== undefined ? item?.hardware_improvement_needed : null,
        documentation_improvement_needed: item?.documentation_improvement_needed !== null && item?.documentation_improvement_needed !== undefined ? item?.documentation_improvement_needed : null,
        comments: item?.comments?.filter(c => c.type === CommentTaskType.PostService) || [],
        attachments: item?.attachments ? objectClone(item?.attachments) : []
    };

    async function onSubmitHandler(values: TaskPostServiceFormData, dirty: FormDirty<TaskPostServiceFormData>, errors?: FormErrors<TaskPostServiceFormData>, submitType?: string | null) {
        const isDirty = Object.values(dirty).reduce((p, c) => p || c, false);

        if (errors) {
            for (var error of Object.values(errors).filter(e => !!e)) {
                enqueueSnackbar(error, { variant: 'error' });
            }
            return;
        }

        if ((values.post_service_status === TaskPostServiceStatus.Concluded || values.post_service_status === TaskPostServiceStatus.Approved) && (
            values.documentation_improvement_needed === null || values.hardware_improvement_needed === null
            || values.software_improvement_needed === null
        )) {
            enqueueSnackbar("Para salvar como concluído ou aprovado, é preciso responder todas as perguntas.", { variant: 'error' });
            return;
        }

        if (values.comments.length === 0 && (
            values.documentation_improvement_needed === 1 || values.hardware_improvement_needed === 1
            || values.software_improvement_needed === 1
        )) {
            enqueueSnackbar("Ao responder SIM em alguma das perguntas é preciso adicionar um comentário.", { variant: 'error' });
            return;
        }

        await onSubmit(values, attachmentsRemoved, isDirty, submitType);
    }

    function openCommentDialog() {
        setCommentDialogOpen(true);
    }

    function closeCommentDialog() {
        setCommentDialogOpen(false);
    }

    function submitCommentDialog(setFieldValue: any, comments: CommentTask[], comment: string, is_public: boolean) {
        if (comment.trim() !== '') {
            let newComment = { id: 0, comment, user_id: user?.id || 0, is_public, type: CommentTaskType.PostService };
            comments.push(newComment);
            eventEmmiter.emit('newComment', newComment);
            setFieldValue('comments', comments);
        }
        setCommentDialogOpen(false);
    }

    function setChanges(changed: boolean) {
        setDirty(changed);
        onChange(changed);
    }

    function addAttachment(attachments: AttachmentTaskRow[], type: AttachmentTaskType, setFieldValue: any, name: string, file?: File, commentIdx?: number, system_attachment_id?: number | null, system_attachment?: any) {
        if (attachments.filter((a) => a.type === type).length < 10) {
            attachments.push({ id: 0, description: '', name, type, file, commentIdx, system_attachment_id, system_attachment });
            setFieldValue('attachments', attachments);
        } else {
            enqueueSnackbar("Limite de 10 anexos atingido.", { variant: 'error', preventDuplicate: true });
        }
    }

    function removeAttachment(idx: number, attachments: AttachmentTaskRow[], setFieldValue: any) {
        let attachment = attachments.find((o, i) => i === idx);
        setFieldValue('attachments', attachments.filter((o, i) => i !== idx));
        if (attachment && attachment.id > 0) {
            attachmentsRemoved.push(attachment);
            setAttachmentsRemoved(attachmentsRemoved);
        }
    }

    async function downloadAttachment(fileName: string) {
        let dir = `Task/${item?.id}/${fileName}`;
        await downloadBlob(dir, fileName);
    }

    function openAttachmentDescriptionDialog(idx: number) {
        setAttachmentsEditingIdx(idx);
        setAttachmentDescriptionDialogOpen(true);
    }

    function closeAttachmentDescriptionDialog() {
        setAttachmentsEditingIdx(null);
        setAttachmentDescriptionDialogOpen(false);
    }

    function submitAttachmentDescriptionDialog(setFieldValue: any, attachments: AttachmentTaskRow[], description: string, idx: number | null) {
        if (idx === null) return;
        attachments[idx].description = description;
        attachments[idx].edited = true;
        setFieldValue('attachments', attachments);
        setAttachmentDescriptionDialogOpen(false);
        setAttachmentsEditingIdx(null);
    }

    async function notApplicablePostService(setFieldValue: any){
        await setFieldValue('post_service_status', TaskPostServiceStatus.Approved);
        await setFieldValue('software_improvement_needed', 0);
        await setFieldValue('hardware_improvement_needed', 0);
        await setFieldValue('documentation_improvement_needed', 0);
    }

    return (
        <Panel
            panelHeaderTitle={title}
            panelFooterButtons={<FormFooterAllButtons onUndo={onUndo} form="task_post_service_form" onBack={onCancel} isDirty={isDirty} />}
        >
            <PanelBodyFormContent style={{ overflow: 'auto' }}>
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={12} md={8} lg={6}>
                        <Form<TaskPostServiceFormData>
                            initialValues={initialValues}
                            onSubmit={onSubmitHandler}
                            onChange={setChanges}
                            style={{ overflow: 'auto' }}
                            id="task_post_service_form"
                        >
                            {({ isSubmitting, values, errors, handleChange, dirty, setFieldValue }) => {
                                return (
                                    <>
                                        <Box marginTop={3} marginBottom={0} className="subtitleGradientBackgorund">
                                            <Typography style={{ flex: 1 }} variant='subtitle2'>
                                                Pós-Atendimento
                                            </Typography>
                                            {values.post_service_status === TaskPostServiceStatus.NotAnalyzed ?
                                                <OrangeButton
                                                    onClick={() => notApplicablePostService(setFieldValue)}
                                                    size='small'
                                                    type='button'
                                                    variant="contained">
                                                    Não aplicável pós-atendimento
                                                </OrangeButton>
                                                : null}
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        <SelectSearch<TaskPostServiceStatus>
                                            name='post_service_status'
                                            label='Status pós-atendimento'
                                            options={[
                                                { label: 'Não analisado', value: TaskPostServiceStatus.NotAnalyzed },
                                                { label: 'Pendente', value: TaskPostServiceStatus.Pending },
                                                { label: 'Concluído', value: TaskPostServiceStatus.Concluded },
                                                { label: 'Aprovado', value: TaskPostServiceStatus.Approved }
                                            ]}
                                            value={values.post_service_status}
                                            onChangeHandle={(_, v) => {
                                                setFieldValue('post_service_status', v || TaskPostServiceStatus.NotAnalyzed);
                                            }}
                                            margin='dense'
                                            fullWidth
                                        />
                                        <SelectSearch<number | null>
                                            name='software_improvement_needed'
                                            label='Necessário melhoria software?'
                                            options={[
                                                { label: 'N/A', value: 0 },
                                                { label: 'Sim', value: 1 },
                                                { label: 'Não', value: 2 }
                                            ]}
                                            value={values.software_improvement_needed}
                                            onChangeHandle={(_, v) => {
                                                setFieldValue('software_improvement_needed', v);
                                            }}
                                            margin='dense'
                                            fullWidth
                                            isClearable
                                        />
                                        <SelectSearch<number | null>
                                            name='hardware_improvement_needed'
                                            label='Necessário melhoria Hardware?'
                                            options={[
                                                { label: 'N/A', value: 0 },
                                                { label: 'Sim', value: 1 },
                                                { label: 'Não', value: 2 }
                                            ]}
                                            value={values.hardware_improvement_needed}
                                            onChangeHandle={(_, v) => {
                                                setFieldValue('hardware_improvement_needed', v);
                                            }}
                                            margin='dense'
                                            fullWidth
                                            isClearable
                                        />
                                        <SelectSearch<number | null>
                                            name='documentation_improvement_needed'
                                            label='Necessário melhoria documentação?'
                                            options={[
                                                { label: 'N/A', value: 0 },
                                                { label: 'Sim', value: 1 },
                                                { label: 'Não', value: 2 }
                                            ]}
                                            value={values.documentation_improvement_needed}
                                            onChangeHandle={(_, v) => {
                                                setFieldValue('documentation_improvement_needed', v);
                                            }}
                                            margin='dense'
                                            fullWidth
                                            isClearable
                                        />
                                        <TaskAttachmentList
                                            attachments={values.attachments}
                                            downloadAttachment={downloadAttachment}
                                            openAttachmentDescriptionDialog={openAttachmentDescriptionDialog}
                                            removeAttachment={removeAttachment}
                                            setFieldValue={setFieldValue}
                                            addAttachment={addAttachment}
                                            type={AttachmentTaskType.PostService}
                                            title={'Anexos do pós-atendimento'}
                                        />
                                        <Box marginTop={3} marginBottom={0} display="flex">
                                            <Typography variant='subtitle2' style={{ flex: 1 }} className="subtitleGradientBackgorund">Histórico do pós-atendimento</Typography>
                                            <Tooltip placement="top" title={'Adicionar comentário'}>
                                                <IconButton
                                                    size='small'
                                                    color="inherit"
                                                    aria-label={'Adicionar comentário'}
                                                    onClick={() => openCommentDialog()}
                                                >
                                                    <ChatIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <TaskAttachmentDescriptionFormDialog
                                            attachmentDescriptionDialogOpen={attachmentDescriptionDialogOpen}
                                            onCancel={() => closeAttachmentDescriptionDialog()}
                                            onSubmit={(description) => submitAttachmentDescriptionDialog(setFieldValue, values.attachments, description, attachmentsEditingIdx)}
                                            attachments={values.attachments}
                                            attachmentsEditingIdx={attachmentsEditingIdx}
                                        />
                                        <CommentFormDialog
                                            commentDialogOpen={commentDialogOpen}
                                            onCancel={() => closeCommentDialog()}
                                            onSubmit={(comment, isPublic) => submitCommentDialog(setFieldValue, values.comments, comment, isPublic)}
                                        />
                                    </>
                                );
                            }}
                        </Form>
                        <Divider style={{ marginBottom: 4 }} />
                        <TicketHistory
                            key={item?.id}
                            type="taskPostService"
                            id={item?.id}
                            comments={item?.comments?.filter(c => c.type === CommentTaskType.PostService) || []}
                            ticketHistoryEventListener={eventEmmiter}
                        />
                    </Grid>
                </Grid>
            </PanelBodyFormContent>
        </Panel>
    )
}